/**
 * Filter bar for the team-scoped /matches page.
 *
 * Two parts:
 *   1. TeamMatchesTabNav — sticky link-based nav matching the competition/
 *      match page pattern (buttons here didn't work reliably; links do).
 *   2. TeamMatchesFilters — competition dropdown.
 *
 * Both preserve unrelated search params on navigation so tab + filters
 * compose naturally.
 */

"use client";

import Link from "next/link";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useCallback } from "react";
import { cn } from "@/utilities/core/cn";

interface TabNavProps {
    tab: "upcoming" | "results";
    labels: { upcoming: string; results: string };
}

export function TeamMatchesTabNav({ tab, labels }: TabNavProps) {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const buildHref = (nextTab: "upcoming" | "results") => {
        const params = new URLSearchParams(searchParams?.toString() ?? "");
        params.set("tab", nextTab);
        params.delete("page");
        const query = params.toString();
        return query ? `${pathname}?${query}` : pathname;
    };

    return (
        <nav className="py-0.5 tab-bar-surface sticky top-below-nav z-10">
            <div className="max-w-350 mx-auto px-4">
                <div className="flex gap-2 my-2 overflow-x-auto scrollbar-hide">
                    {(["upcoming", "results"] as const).map((key) => (
                        <Link
                            key={key}
                            href={buildHref(key)}
                            prefetch={false}
                            className={cn(
                                "px-5 py-3 font-medium text-xs uppercase whitespace-nowrap",
                                tab === key ? "btn-primary" : "link-themed"
                            )}
                        >
                            {labels[key]}
                        </Link>
                    ))}
                </div>
            </div>
        </nav>
    );
}

interface FiltersProps {
    competitionId: string | null;
    competitions: { id: string; name: string }[];
    labels: { allCompetitions: string };
}

export function TeamMatchesFilters({ competitionId, competitions, labels }: FiltersProps) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const updateParam = useCallback(
        (key: string, value: string) => {
            const params = new URLSearchParams(searchParams?.toString() ?? "");
            if (value) params.set(key, value);
            else params.delete(key);
            params.delete("page");
            router.replace(`${pathname}?${params.toString()}`, { scroll: false });
        },
        [pathname, router, searchParams]
    );

    if (competitions.length === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-3 mb-6">
            <select
                value={competitionId ?? ""}
                onChange={(e) => updateParam("competition", e.target.value)}
                className="bg-card border border-border rounded px-3 py-1.5 text-sm font-semibold"
            >
                <option value="">{labels.allCompetitions}</option>
                {competitions.map((c) => (
                    <option key={c.id} value={c.id}>
                        {c.name}
                    </option>
                ))}
            </select>
        </div>
    );
}
